import { supabase } from '../supabaseClient';
import { describeError } from '../errorMessage';
import { istFehlendesSchema } from './sifaService';
import { istPinForm, mapKundenzugang } from './kundenportalLogic';
import type { SifaKundenzugang } from './kundenportalTypes';

/**
 * INNENDIENST-Seite des Kundenbereichs: der Zugang eines SiFa-Projekts
 * (Tabelle `sifa_kundenzugaenge`, Nr. 93) ueber RLS — anlegen, PIN setzen,
 * deaktivieren, Ablauf, Sperre/Fehlversuche zuruecksetzen.
 *
 * ZWEI REGELN aus Nr. 93, die hier festgenagelt sind:
 *  (a) IMMER eine ausdrueckliche Spaltenliste, nie `select('*')`: der Client
 *      hat auf `pin_hash` KEIN Recht (Spaltenrechte); ein `*` scheiterte mit
 *      42501 und die Karte bliebe leer.
 *  (b) Die PIN wird NIE gespeichert und NIE ueber die Tabelle geschrieben:
 *      `sifa_kundenzugang_pin_setzen` (Nr. 94) rechnet den Hash. Der Client
 *      zeigt die PIN genau einmal, beim Erzeugen; danach gibt es sie nicht mehr.
 *
 * Fail-open beim LESEN (Tabelle/RPC fehlt → null), sichtbarer Fehler beim
 * SCHREIBEN (Muster projektDokumenteService).
 */

const TABELLE = 'sifa_kundenzugaenge';

/** Alle Spalten AUSSER pin_hash — siehe (a). */
const SPALTEN =
  'id, project_id, token, aktiv, aktiv_bis, fehlversuche, gesperrt_bis, letzter_zugriff, company_name, created_by, created_at, updated_at';

type Zeile = Record<string, unknown>;

const werfe = (e: unknown): never => {
  throw new Error(describeError(e));
};

/** Firmen-Anzeigename und Nutzer-Id des angemeldeten Profils — best effort, nie blockierend. */
const eigenesProfil = async (): Promise<{ userId: string | null; companyName: string | null }> => {
  try {
    const { data: auth } = await supabase.auth.getUser();
    const userId = auth?.user?.id ?? null;
    if (!userId) return { userId: null, companyName: null };
    const { data } = await supabase.from('profiles').select('company_name').eq('id', userId).maybeSingle();
    const name = (data as { company_name?: string } | null)?.company_name;
    return { userId, companyName: name?.trim() ? name.trim() : null };
  } catch {
    return { userId: null, companyName: null };
  }
};

/** Der Zugang eines Projekts oder null (keiner angelegt ODER Migration Nr. 93 offen). */
export const fetchKundenzugang = async (projectId: string): Promise<SifaKundenzugang | null> => {
  const pid = (projectId || '').trim();
  if (!pid) return null;
  try {
    const { data, error } = await supabase.from(TABELLE).select(SPALTEN).eq('project_id', pid).maybeSingle();
    if (error) throw error;
    return data ? mapKundenzugang(data as Zeile) : null;
  } catch (e) {
    if (istFehlendesSchema(e)) {
      console.warn('sifa_kundenzugaenge fehlt — Migration Nr. 93 noch nicht gefahren.');
      return null;
    }
    return werfe(e);
  }
};

/**
 * Legt den Zugang an (ohne PIN — die kommt ueber `setzeKundenzugangPin`).
 * Das Token vergibt die Datenbank (Spaltendefault). Gibt es schon einen
 * Zugang (Unique je Projekt), kommt der bestehende zurueck statt 23505.
 */
export const createKundenzugang = async (projectId: string): Promise<SifaKundenzugang> => {
  const pid = (projectId || '').trim();
  if (!pid) throw new Error('Ohne Projekt lässt sich kein Kundenzugang anlegen.');
  const profil = await eigenesProfil();
  const { data, error } = await supabase
    .from(TABELLE)
    .insert({ project_id: pid, company_name: profil.companyName, created_by: profil.userId, aktiv: true })
    .select(SPALTEN)
    .single();
  if (error) {
    if ((error as { code?: string }).code === '23505') {
      const vorhanden = await fetchKundenzugang(pid);
      if (vorhanden) return vorhanden;
    }
    return werfe(error);
  }
  return mapKundenzugang(data as Zeile);
};

/**
 * Setzt die PIN (RPC `sifa_kundenzugang_pin_setzen`): schreibt nur den Hash,
 * setzt Fehlversuche und Sperre zurueck. `false` = kein Zugang fuer dieses
 * Projekt. Die PIN selbst behaelt nur der Aufrufer — zum einmaligen Anzeigen.
 */
export const setzeKundenzugangPin = async (projectId: string, pin: string): Promise<boolean> => {
  const pid = (projectId || '').trim();
  if (!pid) throw new Error('Ohne Projekt lässt sich keine PIN setzen.');
  if (!istPinForm(pin)) throw new Error('Die PIN muss aus genau sechs Ziffern bestehen.');
  const { data, error } = await supabase.rpc('sifa_kundenzugang_pin_setzen', { p_project_id: pid, p_pin: pin.trim() });
  if (error) {
    if (istFehlendesSchema(error)) throw new Error('Die PIN-Funktion fehlt auf dem Server (Migration Nr. 94 noch nicht gefahren).');
    return werfe(error);
  }
  return data === true;
};

export interface KundenzugangPatch {
  aktiv?: boolean;
  /** ISO-Zeitstempel oder null = ohne Ablauf. */
  aktivBis?: string | null;
}

/** Deaktivieren/Aktivieren und Ablauf — nur die Spalten, auf die der Client ein Update-Recht hat. */
export const updateKundenzugang = async (id: string, patch: KundenzugangPatch): Promise<SifaKundenzugang> => {
  const zid = (id || '').trim();
  if (!zid) throw new Error('Kundenzugang ohne Id.');
  const felder: Zeile = { updated_at: new Date().toISOString() };
  if (patch.aktiv !== undefined) felder.aktiv = patch.aktiv;
  if (patch.aktivBis !== undefined) felder.aktiv_bis = patch.aktivBis;
  const { data, error } = await supabase.from(TABELLE).update(felder).eq('id', zid).select(SPALTEN);
  if (error) return werfe(error);
  if (!data || data.length === 0) throw new Error('Der Kundenzugang wurde nicht gefunden (Speichern).');
  return mapKundenzugang(data[0] as Zeile);
};

/** Sperre aufheben und Fehlversuche auf null — wenn der Kunde sich ausgesperrt hat. */
export const entsperreKundenzugang = async (id: string): Promise<SifaKundenzugang> => {
  const zid = (id || '').trim();
  if (!zid) throw new Error('Kundenzugang ohne Id.');
  const { data, error } = await supabase
    .from(TABELLE)
    .update({ fehlversuche: 0, gesperrt_bis: null, updated_at: new Date().toISOString() })
    .eq('id', zid)
    .select(SPALTEN);
  if (error) return werfe(error);
  if (!data || data.length === 0) throw new Error('Der Kundenzugang wurde nicht gefunden (Entsperren).');
  return mapKundenzugang(data[0] as Zeile);
};
